import React, { useEffect, useId, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Icon } from '@99designs/frontend-components/components/Icon';
import { colors, spaces, typography } from '../../../lib/styles';
import { getElementHeight } from '../helpers';

const StyledAccordionItem = styled.div`
    border-color: ${colors.typography.contentBorder};
    border-style: solid;
    border-top-width: ${spaces.space2}px;
    border-bottom-width: 0;
    border-left-width: 0;
    border-right-width: 0;

    &:last-of-type {
        border-bottom-width: ${spaces.space2}px;
    }
`;

const ItemButton = styled.button`
    border: none;
    border-radius: 0;
    background: transparent;
    text-align: left;

    &:hover,
    &:focus {
        box-shadow: none;
        text-decoration: none;
        outline: none;
    }

    position: relative;
    width: 100%;
    padding-top: ${spaces.space9}px;
    padding-bottom: ${spaces.space9}px;
    padding-left: ${spaces.space6}px;
    padding-right: ${spaces.space13}px;
`;

const ItemHeading = styled.div`
    display: inline-block;
    vertical-align: middle;
`;

const ItemIndicator = styled.div`
    display: inline-block;
    position: absolute;
    right: ${spaces.space6}px;
    top: 50%;
    transform: translateY(-50%);
    &:before {
        display: block;
        margin: 0;
        line-height: 1;
    }
    font-size: ${typography.typeScale.size5}px;
    color: ${colors.grey500};
`;

const ItemBody = styled.div<{
    isOpen: boolean;
    ref: React.RefObject<HTMLDivElement>;
    bodyHeight: number;
}>(({ isOpen, bodyHeight }) => {
    return css`
        padding-left: ${spaces.space6}px;
        padding-right: ${spaces.space6}px;
        height: 0;
        min-height: 0;
        visibility: hidden;
        opacity: 0;
        overflow: hidden;
        transition: all 0.3s, visibility 0.3s;

        ${isOpen &&
        css`
            height: auto;
            visibility: visible;
            min-height: ${bodyHeight}px;
            opacity: 1;
        `}
    `;
});

export interface Props {
    title: string;
    children: React.ReactNode;
    className?: string;
    onOpen?: () => void;
}

const AccordionItemLegacy = ({ title, children, className = '', onOpen }: Props) => {
    const [isOpen, setIsOpen] = useState(false);
    const elementId = useId().replace(/:/g, '');
    const triggerId = `accordion-trigger-${elementId}`;
    const contentId = `accordion-panel-${elementId}`;

    const [bodyHeight, setBodyHeight] = useState(0);
    const itemBodyRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const element = itemBodyRef.current;
        if (element) {
            setBodyHeight(getElementHeight(element));

            let timeoutId: ReturnType<typeof setTimeout>;

            const resizeListener = () => {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => {
                    element && setBodyHeight(getElementHeight(element));
                }, 300);
            };

            window.addEventListener('resize', resizeListener);

            return () => {
                window.removeEventListener('resize', resizeListener);
            };
        }
        return;
    }, [itemBodyRef]);

    return (
        <StyledAccordionItem data-testid="accordion-item" className={className}>
            <ItemButton
                onClick={() => {
                    if (!isOpen && onOpen) {
                        onOpen();
                    }
                    setIsOpen((isOpen) => !isOpen);
                }}
                aria-controls={contentId}
                aria-expanded={isOpen}
                id={triggerId}
            >
                <ItemHeading>
                    <p className="paragraph paragraph--no-margin paragraph--root">{title}</p>
                </ItemHeading>
                <ItemIndicator>
                    <Icon type={isOpen ? 'minus-2' : 'plus-2'} variations={['inline']} />
                </ItemIndicator>
            </ItemButton>
            <ItemBody
                aria-hidden={!isOpen}
                aria-labelledby={triggerId}
                id={contentId}
                isOpen={isOpen}
                ref={itemBodyRef}
                bodyHeight={bodyHeight}
            >
                {children}
            </ItemBody>
        </StyledAccordionItem>
    );
};

export default AccordionItemLegacy;
